import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Field } from 'formik';
import { Checkbox } from '@upmc/ims.component-library';
import { CSS_CLASSES, FORM_KEYS, USER_TYPES } from './_constants';
import { getUserTypes, getStudentInstitutions, getSubUserTypes } from './_api';
import FieldWrapper from '../../../../Form/FieldWrapper';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextBox from '../../../../Form/TextBox/TextBox';
import { raiseErrorBanner } from '../../../../../components/MessageDispatch/messageDispatch';
import { userTypePropTypes } from './_types';

import classes from './UserType.module.scss';

/**
 * Functional component for collecting a user type and conditional, secondary
 * data.
 *
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const UserType = ({ labelPosition, ...props }) => {
	const dispatch = useDispatch();

	const [sUserTypes, setUserTypes] = useState([]);
	const [sUserType, setUserType] = useState('');
	const [sAgencies, setAgencies] = useState([]);
	const [sAgency, setAgency] = useState('');
	const [sLocations, setLocations] = useState([]);
	const [sInstitutions, setInstitutions] = useState([]);

	/**
	 * Initial render lifecycle hook.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		/**
		 * Retrieves and populates available user types in local state.
		 *
		 * @function
		 * @return void.
		 */
		async function initialize() {
			await getUserTypes()
				.then(async response => {
					let arrUserTypes = response;
					arrUserTypes =
						Array.isArray(arrUserTypes) &&
						props.excludeTypes &&
						Array.isArray(props.excludeTypes)
							? arrUserTypes.filter(
									userType =>
										!props.excludeTypes.includes(
											userType.value.toLocaleLowerCase()
										)
							  )
							: arrUserTypes;
					setUserTypes(arrUserTypes);
				})
				.catch(error => {
					raiseErrorBanner(error, dispatch);
				});
		}
		initialize();
	}, [dispatch, props.excludeTypes]);

	return (
		<Fragment>
			{sUserTypes.length > 1 && (
				<Field name={FORM_KEYS.USER_TYPE}>
					{({ field, meta }) => (
						<FieldWrapper
							name={FORM_KEYS.USER_TYPE}
							label={'Select User Type'}
							meta={meta}
							additionalProps={{
								labelPosition: labelPosition,
							}}
							hideErrorMessage
						>
							<FormControl
								className={classes[CSS_CLASSES.INPUT]}
								error={meta.touched && meta.error}
								required
								variant='filled'
							>
								<InputLabel
									id={FORM_KEYS.USER_TYPE + '__label'}
								>
									User Type
								</InputLabel>
								<Select
									id={FORM_KEYS.USER_TYPE}
									error={meta.touched && meta.error}
									fullWidth
									label='User Type'
									labelId={FORM_KEYS.USER_TYPE + '__label'}
									name={FORM_KEYS.USER_TYPE}
									onBlur={field.onBlur}
									onChange={e => {
										const value = e.target.value;
										if (value) {
											switch (value.toLocaleLowerCase()) {
												case USER_TYPES.REGULATORY_USER:
													if (sAgencies.length < 1) {
														getSubUserTypes(
															14
														).then(response => {
															setAgencies(
																response
															);
														});
													}
													break;
												case USER_TYPES.STUDENT:
													if (
														sInstitutions.length < 1
													) {
														getStudentInstitutions().then(
															response => {
																setInstitutions(
																	response
																);
															}
														);
													}
													break;
												default:
													break;
											}
										}
										field.onChange(e);
										setUserType(value);
									}}
									value={field.value}
									variant='filled'
								>
									{sUserTypes.map(userType => (
										<MenuItem
											key={userType.key}
											value={userType.value}
										>
											{userType.value}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									{meta.touched && meta.error
										? meta.error
										: ' '}
								</FormHelperText>
							</FormControl>
						</FieldWrapper>
					)}
				</Field>
			)}
			{sUserType.toLocaleLowerCase() === USER_TYPES.OTHER && (
				<TextBox
					className={classes[CSS_CLASSES.INPUT]}
					label='Other'
					labelPosition={labelPosition}
					name={FORM_KEYS.USER_TYPE_OTHER}
					inputProps={{
						required: true,
					}}
				/>
			)}
			{sUserType.toLocaleLowerCase() === USER_TYPES.REGULATORY_USER &&
				sAgencies.length > 1 && (
					<Fragment>
						<Field
							name={FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY}
						>
							{({ field, meta }) => (
								<FieldWrapper
									name={
										FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY
									}
									label={'Select Agency'}
									meta={meta}
									additionalProps={{
										labelPosition: labelPosition,
									}}
									hideErrorMessage
								>
									<FormControl
										className={classes[CSS_CLASSES.INPUT]}
										error={meta.touched && meta.error}
										required
										variant='filled'
									>
										<InputLabel
											id={
												FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY +
												'__label'
											}
										>
											Agency
										</InputLabel>
										<Select
											id={
												FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY
											}
											error={meta.touched && meta.error}
											fullWidth
											label='Agency'
											labelId={
												FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY +
												'__label'
											}
											name={
												FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY
											}
											onBlur={field.onBlur}
											onChange={e => {
												field.onChange(e);
												const value = e.target.value;
												setAgency(value);
												if (value) {
													const agency =
														sAgencies.find(
															agency =>
																agency.value ===
																value
														);
													if (agency) {
														getSubUserTypes(
															agency.key
														).then(response =>
															setLocations(
																response
															)
														);
													}
												}
											}}
											value={field.value}
											variant='filled'
										>
											{sAgencies.map(agency => (
												<MenuItem
													key={agency.key}
													value={agency.value}
												>
													{agency.value}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{meta.touched && meta.error
												? meta.error
												: ' '}
										</FormHelperText>
									</FormControl>
								</FieldWrapper>
							)}
						</Field>
						{sAgency && sLocations.length > 1 && (
							<Field
								name={
									FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION
								}
							>
								{({ field, meta }) => (
									<FieldWrapper
										name={
											FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION
										}
										label={'Select Location'}
										meta={meta}
										additionalProps={{
											labelPosition: labelPosition,
										}}
										hideErrorMessage
									>
										<FormControl
											className={
												classes[CSS_CLASSES.INPUT]
											}
											error={meta.touched && meta.error}
											required
											variant='filled'
										>
											<InputLabel
												id={
													FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION +
													'__label'
												}
											>
												Location
											</InputLabel>
											<Select
												id={
													FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION
												}
												error={
													meta.touched && meta.error
												}
												fullWidth
												label='Location'
												labelId={
													FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION +
													'__label'
												}
												name={
													FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION
												}
												onBlur={field.onBlur}
												onChange={field.onChange}
												value={field.value}
												variant='filled'
											>
												{sLocations.map(location => (
													<MenuItem
														key={location.key}
														value={location.value}
													>
														{location.value}
													</MenuItem>
												))}
											</Select>
											<FormHelperText>
												{meta.touched && meta.error
													? meta.error
													: ' '}
											</FormHelperText>
										</FormControl>
									</FieldWrapper>
								)}
							</Field>
						)}
					</Fragment>
				)}
			{sUserType.toLocaleLowerCase() === USER_TYPES.RESEARCHER && (
				<TextBox
					className={classes[CSS_CLASSES.INPUT]}
					label='Additional Information'
					labelPosition={labelPosition}
					name={FORM_KEYS.USER_TYPE_RESEARCHER_INFO}
					inputProps={{
						required: true,
					}}
				/>
			)}
			{sUserType.toLocaleLowerCase() === USER_TYPES.STUDENT &&
				sInstitutions.length > 1 && (
					<Fragment>
						<Field name={FORM_KEYS.USER_TYPE_STUDENT_COLLEGE}>
							{({ field, meta }) => (
								<FieldWrapper
									name={FORM_KEYS.USER_TYPE_STUDENT_COLLEGE}
									label={'Select College'}
									meta={meta}
									additionalProps={{
										labelPosition: labelPosition,
									}}
									hideErrorMessage
								>
									<FormControl
										className={classes[CSS_CLASSES.INPUT]}
										error={meta.touched && meta.error}
										required
										variant='filled'
									>
										<InputLabel
											id={
												FORM_KEYS.USER_TYPE_STUDENT_COLLEGE +
												'__label'
											}
										>
											College
										</InputLabel>
										<Select
											id={
												FORM_KEYS.USER_TYPE_STUDENT_COLLEGE
											}
											error={meta.touched && meta.error}
											fullWidth
											label='College'
											labelId={
												FORM_KEYS.USER_TYPE_STUDENT_COLLEGE +
												'__label'
											}
											name={
												FORM_KEYS.USER_TYPE_STUDENT_COLLEGE
											}
											onBlur={field.onBlur}
											onChange={e => {
												field.onChange(e);
											}}
											value={field.value}
											variant='filled'
										>
											{sInstitutions.map(institution => (
												<MenuItem
													key={institution.key}
													value={institution.value}
												>
													{institution.value}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{meta.touched && meta.error
												? meta.error
												: ' '}
										</FormHelperText>
									</FormControl>
								</FieldWrapper>
							)}
						</Field>
					</Fragment>
				)}
			{sUserType.toLocaleLowerCase() === USER_TYPES.VENDOR && (
				<FieldWrapper
					name={FORM_KEYS.USER_TYPE_VENDOR_ACCESS}
					label='Will Access Hospitals (and / or) Surgery Centers'
					additionalProps={{ labelPosition: labelPosition }}
				>
					<Field name={FORM_KEYS.USER_TYPE_VENDOR_ACCESS}>
						{({ field: { onChange } }) => (
							<Checkbox
								label='Will Access Hospitals (and / or) Surgery Centers'
								name={FORM_KEYS.USER_TYPE_VENDOR_ACCESS}
								onChange={e => {
									onChange({
										target: {
											name: FORM_KEYS.USER_TYPE_VENDOR_ACCESS,
											value: e.target.checked,
										},
									});
								}}
							/>
						)}
					</Field>
				</FieldWrapper>
			)}
		</Fragment>
	);
};

UserType.propTypes = userTypePropTypes;

export { UserType };
